import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { API_CONFIG } from '../../../config/api';
import { operationalComponentSurfaces } from '../../../design-system/tokens';

const MENSURE_FALLBACK_IMAGE = '/mensure-thumbnail.png';
const MENSURE_FALLBACK_REMOTE_IMAGE = 'https://media.mensuremidia.com.br/system/fallbacks/mensure-thumbnail.png';

/**
 * ArticleImage - Componente atômico para exibir a imagem de um artigo
 * Agora aceita src como string (URL) ou objeto (ex: article) e faz fallback automático.
 *
 * Para mídia impressa (Jornal/Revista), prioriza thumbnail sobre image para
 * mostrar uma área relevante da página (geralmente o terço superior).
 */
/**
 * Adiciona proxy para imagens do Instagram/Threads CDN que têm problemas de CORS
 */
const addProxyIfNeeded = (url) => {
  if (!url) return url;
  if (isMensureFallbackThumbnail(url)) return MENSURE_FALLBACK_IMAGE;

  // Se a URL já é um proxy, retornar como está
  if (url.includes('/api/proxy/')) return url;

  // Detectar URLs do Instagram/Threads/Facebook CDN que precisam de proxy
  const needsProxy = url.includes('fbcdn.net') ||
    url.includes('cdninstagram.com') ||
    url.includes('instagram.com') ||
    url.includes('scontent');

  if (needsProxy) {
    // Usar a configuração centralizada da API que detecta automaticamente produção
    return `${API_CONFIG.BASE_URL}/proxy/instagram-image?url=${encodeURIComponent(url)}`;
  }

  return url;
};

const isMensureFallbackThumbnail = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('/mensure-thumbnail.png') ||
    url.includes(MENSURE_FALLBACK_REMOTE_IMAGE);
};

const isMensureLegacyLogo = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('logo-mensure.png') ||
    url.includes('mensure.com.br/assets/images/logo') ||
    url.includes('LogoMensureAvatarLaranja.svg') ||
    url.includes('mensuremidia.com.br/cliente/mensure/miv');
};

const isMensureFallbackImage = (url) => (
  isMensureFallbackThumbnail(url) || isMensureLegacyLogo(url)
);

const pushImageCandidate = (candidates, url, { allowMensureFallback = false } = {}) => {
  if (!url) return;
  if (!allowMensureFallback && isMensureFallbackImage(url)) return;
  candidates.push(addProxyIfNeeded(url));
};

const getArticleImageCandidates = (src) => {
  if (!src || typeof src !== 'object') return [];

  const candidates = [];
  const fallbackCandidates = [];
  const isPrintMedia = src.mediaType === 'Jornal' || src.mediaType === 'Revista';

  // Impresso usa thumbnail como recorte principal. Online/social deve preferir
  // a imagem real do artigo; thumbnail pode vir de metadados genericos do site.
  if (isPrintMedia) {
    pushImageCandidate(candidates, src.thumbnail);
  }

  pushImageCandidate(candidates, src.imageUrl || src.image || src.img);

  if (Array.isArray(src.allImages)) {
    src.allImages.forEach((url) => pushImageCandidate(candidates, url));
  }

  if (Array.isArray(src.carouselMedia)) {
    src.carouselMedia.forEach((m) => {
      if (m.type !== 'video') {
        pushImageCandidate(candidates, m.src || m.url);
      }
    });
  }

  if (!isPrintMedia) {
    pushImageCandidate(candidates, src.thumbnail);
  }

  if (isPrintMedia) {
    pushImageCandidate(fallbackCandidates, src.thumbnail, { allowMensureFallback: true });
  }
  ['imageUrl', 'image', 'img', 'thumbnail'].forEach((key) => {
    pushImageCandidate(fallbackCandidates, src[key], { allowMensureFallback: true });
  });

  return [...new Set([...candidates, ...fallbackCandidates])];
};

const getImageSource = (src) => {
  if (typeof src === 'string') return addProxyIfNeeded(src);
  if (typeof src === 'object' && src !== null) {
    return getArticleImageCandidates(src)[0] || '';
  }
  return '';
};

/**
 * Retorna lista ordenada de URLs candidatas para um artigo (usada no fallback progressivo de erro).
 * Ordem: thumbnail/primary reais -> allImages[] -> carouselMedia -> fallback Mensure
 */
const getFallbackSources = (src) => {
  if (!src || typeof src !== 'object') return [];
  return getArticleImageCandidates(src);
};

const isDirectVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m3u8)$/i.test(clean);
};

const hasVideo = (src) => {
  if (typeof src === 'object' && src !== null) {
    // Em algumas plataformas (ex: Twitter/X), videoUrl pode ser a URL do post
    // e nao um arquivo de video direto. Nesses casos, o card deve usar thumbnail.
    return isDirectVideoUrl(src.videoUrl);
  }
  return false;
};

const isRadioArticle = (src) => {
  if (!src || typeof src !== 'object') return false;
  return src.mediaType === 'Radio' || src.mediaType === 'Rádio';
};

const getRadioSeed = (src) => {
  const source = `${src?.title || ''}${src?.siteName || ''}${src?._id || ''}`;
  return source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
};

const RadioWaveform = ({ article, variant, className, sx, ...props }) => {
  const seed = getRadioSeed(article);
  const barCount = variant === 'list' ? 44 : 56;
  const bars = Array.from({ length: barCount }, (_, index) => {
    const wave = Math.sin((index + seed) * 0.72) + Math.sin((index + seed) * 0.23);
    const normalized = (wave + 2) / 4;
    const accentLift = index % 7 === 0 ? 12 : 0;
    return Math.round(18 + normalized * 58 + accentLift);
  });

  const variantStyles = {
    default: { width: '120px', height: '80px' },
    small: { width: '80px', height: '60px' },
    large: { width: '100%', height: '160px' },
    thumbnail: { width: '48px', height: '48px' },
    cover: { width: '100%', height: '200px' },
    detailed: { width: '100%', height: '220px' },
    list: { width: '240px', height: '140px' },
    minimal: { width: '100%', height: '120px' }
  };

  return (
    <Box
      className={`article-image article-image--radio-waveform article-image--${variant} ${className}`}
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #15191c 0%, #1e2b2f 54%, #102724 100%)',
          color: 'rgba(248, 250, 252, 0.94)',
          ...variantStyles[variant],
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 18% 18%, rgba(34, 197, 94, 0.12), transparent 32%), radial-gradient(circle at 86% 72%, rgba(14, 165, 233, 0.1), transparent 34%)',
            pointerEvents: 'none'
          }
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      {...props}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, sm: 2.4 },
          pt: { xs: 5.2, sm: 5.8 },
          pb: { xs: 2.1, sm: 2.4 }
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            width: '100%',
            height: '100%',
            minHeight: 0,
            opacity: 0.96
          }}
        >
          {bars.map((height, index) => (
            <Box
              key={`${height}-${index}`}
              sx={{
                flex: '1 1 0',
                minWidth: 2,
                maxWidth: 7,
                height: `${height}%`,
                borderRadius: '999px',
                backgroundColor: index < Math.round(barCount * 0.44)
                  ? 'rgba(45, 212, 191, 0.96)'
                  : 'rgba(226, 232, 240, 0.46)',
                boxShadow: index < Math.round(barCount * 0.44)
                  ? '0 0 14px rgba(45, 212, 191, 0.24)'
                  : 'none',
                transformOrigin: 'center',
                animation: `radioWavePulse 2.8s ease-in-out ${index * 0.035}s infinite`
              }}
            />
          ))}
        </Box>
      </Box>

      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes radioWavePulse {
              0%, 100% { transform: scaleY(0.92); opacity: 0.88; }
              50% { transform: scaleY(1); opacity: 1; }
            }
          `
        }}
      />
    </Box>
  );
};

RadioWaveform.propTypes = {
  article: PropTypes.object,
  variant: PropTypes.string,
  className: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func])
};

/**
 * ArticleImage - Componente atômico para exibir a imagem de um artigo
 * 
 * Este componente segue o princípio do Atomic Design como um "átomo".
 * Exibe a imagem do artigo com diferentes variantes de tamanho e estilo.
 */
const ArticleImage = ({
  src,
  alt,
  variant = 'default',
  className = '',
  onError,
  ...props
}) => {
  const imageSource = getImageSource(src);
  const [currentSrc, setCurrentSrc] = useState(imageSource);
  const triedSources = useRef(new Set());
  const [hasError, setHasError] = useState(false);

  // Quando o src mudar (ex: artigo diferente), resetar estado
  React.useEffect(() => {
    const newSource = getImageSource(src);
    setCurrentSrc(newSource);
    setHasError(false);
    triedSources.current = new Set();
  }, [
    typeof src === 'string' ? src : (
      src?.imageUrl || src?.image || src?.img || src?.thumbnail ||
      (src?.allImages?.[0]) || ''
    )
  ]);

  const imageUrl = currentSrc || null;
  const isVideo = hasVideo(src);
  const videoUrl = isVideo ? (typeof src === 'object' ? src.videoUrl : null) : null;
  const isRadio = isRadioArticle(src);

  // Detectar se a imagem é o logo da Mensure (fallback)
  const isMensureLogo = currentSrc && isMensureLegacyLogo(currentSrc);


  // Configurações de estilo para cada variante
  const variantStyles = {
    default: { width: '120px', height: '80px' },
    small: { width: '80px', height: '60px' },
    large: { width: '100%', height: '160px' },
    thumbnail: { width: '48px', height: '48px' },
    cover: { width: '100%', height: '200px' },
    // Adicionando variantes necessárias para os diferentes cards
    detailed: { width: '100%', height: '220px' },
    list: { width: '240px', height: '140px' },
    minimal: { width: '100%', height: '120px' }
  };

  const styles = {
    container: {
      overflow: 'hidden',
      // Fundo claro quando for o logo da Mensure (laranja), caso contrário cinza claro
      backgroundColor: isMensureLogo
        ? operationalComponentSurfaces.media.logo
        : operationalComponentSurfaces.media.placeholder,
      position: 'relative',
      boxSizing: 'border-box',
      padding: isMensureLogo ? '12px' : 0,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...variantStyles[variant]
    },
    placeholder: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: operationalComponentSurfaces.media.placeholderRaised
    },
    icon: {
      fontSize: '1.5rem',
      color: operationalComponentSurfaces.chip.light.text
    },
    image: {
      width: isMensureLogo ? 'auto' : '100%',
      height: isMensureLogo ? 'auto' : '100%',
      maxWidth: isMensureLogo ? '85%' : '100%',
      maxHeight: isMensureLogo ? '85%' : '100%',
      // Todas as imagens agora usam 'cover' para manter consistência visual
      objectFit: 'cover',
      boxSizing: 'border-box',
      display: 'block'
    }
  };

  if (isRadio) {
    return (
      <RadioWaveform
        article={src}
        variant={variant}
        className={className}
        {...props}
      />
    );
  }

  // Handler progressivo de erro: tenta todas as fontes alternativas antes de desistir
  const handleImageError = (e) => {
    const failedUrl = e.target.src;
    triedSources.current.add(failedUrl);

    // Percorre todas as fontes candidatas e usa a primeira ainda não tentada
    const candidates = getFallbackSources(src);
    const next = candidates.find((url) => !triedSources.current.has(url));

    if (next) {
      setCurrentSrc(next);
      return;
    }

    // Esgotou todas as alternativas → mostra fallback Mensure
    setHasError(true);
    if (onError) onError(e);
  };

  // Se não houver imagem ou houver erro definitivo, retorna um placeholder (nossa thumbnail padrão)
  if (!imageUrl || hasError) {
    return (
      <Box
        className={`article-image article-image--placeholder ${className}`}
        sx={styles.container}
        {...props}
      >
        <img
          src={MENSURE_FALLBACK_IMAGE}
          alt="Imagem não disponível"
          className="article-image__img"
          style={{
            ...styles.image,
            borderRadius: 0
          }}
          loading="lazy"
        />
      </Box>
    );
  }


  // Se For video (caso exista local ou no Cloudinary)
  if (isVideo && videoUrl) {
    // Adiciona #t=0.1 se for mp4/webm e ainda não tiver fragmento de tempo, para o navegador renderizar o primeiro frame
    let videoSrc = videoUrl;
    if ((videoSrc.includes('.mp4') || videoSrc.includes('.webm')) && !videoSrc.includes('#t=')) {
      videoSrc = `${videoSrc}#t=0.1`;
    }

    return (
      <Box
        className={`article-image article-image--${variant} ${className}`}
        sx={styles.container}
        {...props}
      >
        <video
          src={videoSrc}
          className="article-image__video"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none' // Não permitir interação
          }}
          muted
          loop
          playsInline
          preload="metadata"
          onError={handleImageError}
        />
        {/* Ícone de play opcional */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: operationalComponentSurfaces.mediaControl.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            '&::before': {
              content: '""',
              width: 0,
              height: 0,
              borderLeft: '20px solid white',
              borderTop: '12px solid transparent',
              borderBottom: '12px solid transparent',
              marginLeft: '4px'
            }
          }}
        />
      </Box>
    );
  }

  // Para imagens ou outros tipos, mostrar imagem normal
  return (
    <Box
      className={`article-image article-image--${variant} ${className}`}
      sx={styles.container}
      {...props}
    >
      <img
        src={imageUrl}
        alt={alt || 'Imagem do artigo'}
        className="article-image__img"
        style={{
          ...styles.image,
          borderRadius: 0
        }}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={handleImageError}
      />
    </Box>
  );
};

ArticleImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  alt: PropTypes.string,
  variant: PropTypes.oneOf([
    'default', 'small', 'large', 'thumbnail', 'cover',
    'detailed', 'list', 'minimal'
  ]),
  className: PropTypes.string,
  onError: PropTypes.func
};

export default React.memo(ArticleImage, (prev, next) => {
  // Re-renderizar se src relevante, variant ou className mudarem
  const getSrcKey = (s) => {
    if (typeof s === 'string') return s;
    if (!s) return '';
    return (
      (s.mediaType || '') +
      '|' + (s.duration || '') +
      '|' + (s.durationSeconds || '') +
      '|' + (s.siteName || s.source || '') +
      '|' +
      (s.imageUrl || s.image || s.img || s.thumbnail || '') +
      '|' + (s.allImages?.[0] || '') +
      '|' + (s.carouselMedia?.[0]?.src || s.carouselMedia?.[0]?.url || '')
    );
  };
  return (
    getSrcKey(prev.src) === getSrcKey(next.src) &&
    prev.variant === next.variant &&
    prev.className === next.className
  );
});
